import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import Dropdown from './Dropdown';

export default function AnswerQuestionModal({
  isOpen,
  onClose,
  question,
  onAnswerSubmitted,
  products = [],
  categories = []
}) {
  const [answer, setAnswer] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setAnswer('');
      // Set initial values from question if available
      setSelectedProductId(question?.product_id || '');
      setSelectedCategory(question?.category || '');
    }
  }, [isOpen, question]);

  // Product options for dropdown
  const productOptions = useMemo(() => {
    return [
      { value: '', label: 'No Product' },
      ...products.map(product => ({
        value: product.id,
        label: product.name
      }))
    ];
  }, [products]);

  // Category options for dropdown
  const categoryOptions = useMemo(() => {
    return [
      { value: '', label: 'No Category' },
      ...categories.map(category => ({
        value: category,
        label: category
      }))
    ];
  }, [categories]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate answer
    if (!answer.trim()) {
      toast.error('Please enter an answer');
      return;
    }

    setIsLoading(true);

    try {
      // TODO: Replace with actual API call when backend is ready
      // const response = await questionsApi.answer({
      //   questionId: question.id,
      //   answer: answer.trim(),
      //   productId: selectedProductId || null,
      //   category: selectedCategory || null
      // });

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));

      toast.success('Answer submitted successfully!');

      // Notify parent component with product and category
      if (onAnswerSubmitted) {
        onAnswerSubmitted(question.id, answer.trim(), {
          productId: selectedProductId || null,
          category: selectedCategory || null
        });
      }

      onClose();
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error('Failed to submit answer. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return; // Prevent closing while submitting
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleClose}
    >
      <div
        className="bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 border-2 border-gray-700/50 rounded-2xl shadow-2xl max-w-3xl w-full h-[90vh] overflow-hidden flex flex-col relative"
        style={{ color: 'var(--color-text-on-accent)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gradient accent border glow */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600/20 via-blue-600/20 to-purple-600/20 rounded-2xl blur opacity-30"></div>

        {/* Content wrapper */}
        <div className="relative bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 rounded-2xl flex flex-col h-full" style={{ color: 'var(--color-text-on-accent)' }}>
                  {/* Header */}
          <div className="px-8 py-5 border-b border-gray-700/50 flex items-center justify-between bg-gradient-to-r from-green-500/10 via-transparent to-blue-500/10 flex-shrink-0" style={{ color: 'var(--color-text-on-accent)' }}>
                      <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-green-600 to-green-700 shadow-lg shadow-green-500/30" style={{ color: 'var(--color-text-on-accent)' }}>
                              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">Answer Question</h2>
                                <p className="text-sm text-gray-400 mt-0.5">Teach the AI to help others with similar questions</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="p-2.5 rounded-xl text-gray-400 hover:text-white hover:bg-gray-700/50 transition-all duration-200 disabled:opacity-50"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-8 space-y-6">
            {/* Question Display */}
            <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-xl p-5 shadow-lg" style={{ color: 'var(--color-text-on-accent)' }}>
                          <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <h3 className="text-sm font-bold text-purple-400 uppercase tracking-wide">Question</h3>
              </div>
              <p className="text-white font-medium text-lg leading-relaxed">{question?.title}</p>
            </div>

            {/* Product and Category Selection */}
            <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/30 rounded-xl p-6 shadow-lg" style={{ color: 'var(--color-text-on-accent)' }}>
                          <div className="grid grid-cols-2 gap-5">
                <div className="flex-1">
                  <label className="block text-sm font-bold text-blue-300 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                    </svg>
                    Product
                  </label>
                  <Dropdown
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                    options={productOptions}
                    disabled={isLoading}
                    className="!py-4 !px-5 !text-lg !bg-gray-900/80 !border-2 !border-blue-500/30 hover:!border-blue-500/50 focus:!border-blue-500 focus:!ring-4 focus:!ring-blue-500/20 !rounded-xl !shadow-md !w-full"
                  />
                </div>

                <div className="flex-1">
                  <label className="block text-sm font-bold text-blue-300 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                    </svg>
                    Category
                  </label>
                  <Dropdown
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    options={categoryOptions}
                    disabled={isLoading}
                    className="!py-4 !px-5 !text-lg !bg-gray-900/80 !border-2 !border-blue-500/30 hover:!border-blue-500/50 focus:!border-blue-500 focus:!ring-4 focus:!ring-blue-500/20 !rounded-xl !shadow-md !w-full"
                  />
                </div>
              </div>
            </div>

            {/* Answer Form */}
            <form onSubmit={handleSubmit}>
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-5 shadow-lg" style={{ color: 'var(--color-text-on-accent)' }}>
                              <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide">
                    Your Answer <span className="text-red-400">*</span>
                  </h3>
                </div>
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Provide a detailed, helpful answer here..."
                  rows={10}
                  disabled={isLoading}
                  className="w-full px-4 py-4 bg-gray-900/80 border-2 border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-4 focus:ring-green-500/20 transition-all resize-none disabled:opacity-50 text-base leading-relaxed"
                />
                <div className="flex items-start gap-2 mt-3">
                  <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Provide a clear, comprehensive answer with examples or steps when applicable. Your answer will train the AI to help future users with similar questions.
                  </p>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="px-8 py-5 border-t border-gray-700/50 flex items-center justify-between bg-gradient-to-r from-gray-800/50 to-gray-900/50 flex-shrink-0" style={{ color: 'var(--color-text-on-accent)' }}>
                      <p className="text-sm text-gray-400 flex items-center gap-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Fill in all fields to submit
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-300 hover:text-white bg-gray-700/50 hover:bg-gray-700 border border-gray-600/50 hover:border-gray-500 transition-all duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isLoading || !answer.trim()}
                className="px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 transition-all duration-200 shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-2 transform hover:scale-[1.02] active:scale-[0.98]"
                style={{ color: 'var(--color-text-on-accent)' }}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting Answer...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Submit Answer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
